import React, { createContext, FC, useReducer, useEffect } from 'react';
import { getLogger } from '@packages/core/logging';
import { GetCVTsResponse } from '@packages/models/api/cvt';
import { CvtActionDispatcher, cvtReducer, CvtState, initialState, CvtActionsList } from './cvt.state';
import { CvtService } from './cvt.service';

const logger = getLogger('cvt');

export interface GetCVTParams {
    endDate?: string;
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: string;
    startDate?: string;
    statuses?: string[];
}
interface CvtProviderProps {
    children: React.ReactNode;
    cvtService: CvtService;
    vin?: string;
}

export const CvtStateContext = createContext<CvtState | undefined>(undefined);
export const CvtDispatchContext = createContext<CvtActionDispatcher | undefined>(undefined);

export const CvtProvider: FC<CvtProviderProps> = ({ children, cvtService, vin }) => {
    const [state, dispatch] = useReducer(cvtReducer, initialState);

    useEffect(() => {
        if (vin) {
            const abortController = new AbortController();

            const loadCvtList = async () => {
                dispatch({ type: CvtActionsList.IS_LOADING });
                try {
                    const queryString = new URLSearchParams(window.location.search);
                    const response = await cvtService.getCvtList(vin, queryString.toString(), true);
                    dispatch({
                        type: CvtActionsList.SET_CVT_LIST,
                        payload: { cvtList: response.data as GetCVTsResponse },
                    });
                    dispatch({ type: CvtActionsList.HAS_LOADED });
                } catch (e) {
                    const err = e as Error;
                    dispatch({ type: CvtActionsList.HAS_ERRORS, payload: { errors: [err.message] } });
                    dispatch({ type: CvtActionsList.HAS_LOADED });
                    dispatch({ type: CvtActionsList.CLEAR_ERRORS });
                    logger.error('Failed to fetch the CVT list', { error: e, vin });
                }
            };

            loadCvtList();

            return () => {
                abortController.abort();
            };
        }
    }, [cvtService, vin]);

    return (
        <CvtStateContext.Provider value={state}>
            <CvtDispatchContext.Provider value={dispatch}>{children}</CvtDispatchContext.Provider>
        </CvtStateContext.Provider>
    );
};

export const toQueryString = (params: GetCVTParams = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach((val) => query.append(key, val));
        } else if (value !== undefined) {
            query.append(key, String(value));
        }
    });
    return query.toString();
};

export async function fetchCvtList(
    dispatch: CvtActionDispatcher,
    cvtService: CvtService,
    vin: string,
    params?: GetCVTParams | string,
    skipLoading?: boolean
) {
    if (!skipLoading) {
        dispatch({ type: CvtActionsList.IS_LOADING });
    }

    try {
        const query = typeof params === 'string' ? params : toQueryString(params);
        const response = await cvtService.getCvtList(vin, query, true);

        dispatch({ type: CvtActionsList.SET_CVT_LIST, payload: { cvtList: response.data as GetCVTsResponse } });
        dispatch({ type: CvtActionsList.HAS_LOADED });
    } catch (e) {
        const err = e as Error;
        dispatch({ type: CvtActionsList.HAS_ERRORS, payload: { errors: [err.message] } });
        dispatch({ type: CvtActionsList.HAS_LOADED });
        dispatch({ type: CvtActionsList.CLEAR_ERRORS });
        logger.error('Failed to fetch the CVT list', { error: e, vin });
    }
}

export function clearCvtErrors(dispatch: CvtActionDispatcher) {
    dispatch({ type: CvtActionsList.CLEAR_ERRORS });
}

export async function deleteCvt(
    dispatch: CvtActionDispatcher,
    cvtService: CvtService,
    diagnosticId: string,
    currentList: GetCVTsResponse
): Promise<boolean> {
    const previousList = currentList?.results ?? [];

    dispatch({ type: CvtActionsList.REMOVE_CVT, payload: { diagnosticId } });
    dispatch({ type: CvtActionsList.IS_LOADING });

    try {
        await cvtService.deleteCvt(diagnosticId);
        return true;
    } catch (e: any) {
        dispatch({
            type: CvtActionsList.SET_CVT_LIST,
            payload: { cvtList: { ...currentList, results: previousList } },
        });
        const err = e as Error;
        dispatch({ type: CvtActionsList.HAS_ERRORS, payload: { errors: [err.message] } });
        return false;
    } finally {
        dispatch({ type: CvtActionsList.HAS_LOADED });
        dispatch({ type: CvtActionsList.CLEAR_ERRORS });
    }
}
